import type { LinksData } from '@jouzen/ecom-components';
import {
  BREAKPOINTS_V3,
  LinksCategoryId,
  useMediaQuery,
} from '@jouzen/ecom-components';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { POP_PATHNAME } from '@/app/consts/pathnames';

import useHeaderConfig from './useHeaderConfig';

type ShopButtonProps =
  | false
  | {
      label: string;
      href: string;
    };

interface GetShopButton {
  headerLinks: LinksData[] | undefined;
  isXLargeScreen: boolean | undefined;
  pathname: string;
  t: (arg: MessageKey) => string;
}

function getShopButton({
  headerLinks,
  isXLargeScreen,
  pathname,
  t,
}: GetShopButton): ShopButtonProps {
  const hideShopButton = !isXLargeScreen && pathname === POP_PATHNAME;
  const statsigShopButton = headerLinks?.find(
    ({ id }) => id === LinksCategoryId.ShopCategory,
  )?.link;

  return !hideShopButton
    ? {
        href: statsigShopButton?.href ?? POP_PATHNAME,
        label: statsigShopButton?.label
          ? t(statsigShopButton.label)
          : t('shop'),
      }
    : false;
}
interface ShopButtonHook {
  shopButton: ShopButtonProps;
  observeNode: (node: HTMLDivElement | null) => void;
}

const useShopButton = (): ShopButtonHook => {
  const [shopButton, setShopButton] = useState<ShopButtonProps>(false);
  const t = useTranslations();
  const pathname = usePathname();
  const headerLinks = useHeaderConfig(pathname);
  const resizeObserverRef = useRef<ResizeObserver>();
  const mounted = useRef(false);
  const isXLargeScreen = useMediaQuery(
    `(min-width: ${BREAKPOINTS_V3.xLarge}px)`,
  );

  const observeNode = (node: HTMLDivElement | null) => {
    if (node) {
      // Cleanup previous observers
      resizeObserverRef.current?.disconnect();

      resizeObserverRef.current = new window.ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (pathname === POP_PATHNAME && rect?.width) {
          const _shopButton = getShopButton({
            headerLinks,
            isXLargeScreen: rect.width >= BREAKPOINTS_V3.xLarge,
            pathname,
            t,
          });
          setShopButton(_shopButton);
        }
      });

      resizeObserverRef.current.observe(node);
    }
  };

  useEffect(() => {
    if (mounted.current) return;
    const _shopButton = getShopButton({
      headerLinks,
      isXLargeScreen,
      pathname,
      t,
    });
    setShopButton(_shopButton);
    mounted.current = true;
  }, [headerLinks, isXLargeScreen, pathname, setShopButton, t]);

  return { shopButton, observeNode };
};

export default useShopButton;
